import React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { fmtMoney, fmtDate } from "@/lib/format";
import {
  printRepaymentsReceived,
  printTodaysEmi,
  printTodayCollection,
  printOutstandingBalance,
  printPrincipalOutstanding,
  printInterestOutstanding,
  printTotalMoneyLent,
  printInterestProfit,
  printPrincipalCollected,
  printInterestCollected,
  printRenewedLoans,
  printPrincipalRecyclingHistory,
  printInterestRecyclingHistory,
  printActiveLoans,
} from "@/lib/print";
import {
  Banknote, TrendingUp, Wallet, AlertTriangle, Users, HandCoins, ArrowRight, Landmark, Percent,
  CalendarClock, CalendarRange, Coins, Smartphone, RefreshCw, Printer,
} from "lucide-react";

const CARD_DEFS = {

  customer_list: {
    label: "Customers List",
    icon: Users,
    color: "text-navy",
    bg: "bg-slate-100",
    money: false,
  },

  total_lent: { label: "Total Money Lent", icon: Banknote, color: "text-navy", bg: "bg-navy-light" },
  total_received: { label: "Repayments Received", icon: HandCoins, color: "text-emerald-700", bg: "bg-emerald-50" },
  profit_earned: { label: "Interest / Profit", icon: TrendingUp, color: "text-maroon", bg: "bg-maroon-light" },
  principal_collected: { label: "Principal Collected", icon: Landmark, color: "text-navy", bg: "bg-navy-light" },
  interest_collected: { label: "Net Interest Collected", icon: Percent, color: "text-maroon", bg: "bg-maroon-light" },
  today_collection: { label: "Today's Collection", icon: CalendarClock, color: "text-emerald-700", bg: "bg-emerald-50" },
  weekly_collection: { label: "Weekly Collection", icon: CalendarRange, color: "text-accentblue", bg: "bg-accentblue-light" },
  cash_collected: { label: "Cash Collected", icon: Coins, color: "text-navy", bg: "bg-slate-100" },
  upi_collected: { label: "UPI Collected", icon: Smartphone, color: "text-accentblue", bg: "bg-accentblue-light" },
  principal_outstanding: {
    label: "Principal Outstanding",
    icon: Landmark,
    color: "text-navy",
    bg: "bg-navy-light",
  },
  interest_outstanding: {
    label: "Interest Outstanding",
    icon: Percent,
    color: "text-maroon",
    bg: "bg-maroon-light",
  },
  outstanding: { label: "Outstanding Balance", icon: Wallet, color: "text-accentblue", bg: "bg-accentblue-light" },
  overdue: { label: "Overdue Amount", icon: AlertTriangle, color: "text-red-600", bg: "bg-red-50" },
  active_borrowers: { label: "Active Borrowers", icon: Users, color: "text-navy", bg: "bg-slate-100", money: false },
  closed_loans_count: { label: "Closed Loans", icon: Landmark, color: "text-emerald-700", bg: "bg-emerald-50", money: false },
    active_loans_count: { label: "Total Active Loans", icon: Landmark, color: "text-navy", bg: "bg-slate-100", money: false },
  todays_emi: { label: "Today's EMI", icon: CalendarClock, color: "text-accentblue", bg: "bg-accentblue-light" },
  processing_fee: { label: "Processing Fee", icon: Coins, color: "text-navy", bg: "bg-navy-light" },
};

export default function Dashboard() {
  const { user } = useAuth();
  const showProcessingFee = user?.business_id === "legacy";
  const { settings, business, currency, dateFormat } = useSettings();
  const [stats, setStats] = useState(null);
  const [closedLoansCount, setClosedLoansCount] = useState(0);
  const [details, setDetails] = useState(null);
  const [renewedLoans, setRenewedLoans] = useState(null);
  const [showRenewedLoans, setShowRenewedLoans] = useState(false);
  const [todaysEmi, setTodaysEmi] = useState(null);
const [processingFeeTotal, setProcessingFeeTotal] = useState(0);
  const [todayCollectionCardTotal, setTodayCollectionCardTotal] = useState(0);
  const [emiDate, setEmiDate] = useState(new Date().toLocaleDateString("en-CA"));
  const [emiStartDate, setEmiStartDate] = useState(new Date().toLocaleDateString("en-CA"));
  const [emiEndDate, setEmiEndDate] = useState(new Date().toLocaleDateString("en-CA"));
  const [weeklyCollectionStartDate, setWeeklyCollectionStartDate] = useState(new Date().toLocaleDateString("en-CA"));
  const [weeklyCollectionEndDate, setWeeklyCollectionEndDate] = useState(new Date().toLocaleDateString("en-CA"));
  const [todayCollectionStartDate, setTodayCollectionStartDate] = useState(new Date().toLocaleDateString("en-CA"));
  const [todayCollectionEndDate, setTodayCollectionEndDate] = useState(new Date().toLocaleDateString("en-CA"));
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [sortOrder, setSortOrder] = useState("az");

  // Principal Recycling - completely separate from existing dashboard cards.
  const [principalRecycling, setPrincipalRecycling] = useState(null);
  const [recyclingAmount, setRecyclingAmount] = useState("");
  const [recyclingDate, setRecyclingDate] = useState(new Date().toLocaleDateString("en-CA"));
  const [recyclingNotes, setRecyclingNotes] = useState("");
  const [recyclingLoading, setRecyclingLoading] = useState(false);
  const [showRecycleHistory, setShowRecycleHistory] = useState(false);

  // INTEREST RECYCLING - SEPARATE MODULE
  // Existing dashboard/card/popup formulas are NOT modified.
  const [interestRecycling, setInterestRecycling] = useState(null);
  const [interestRecyclingAmount, setInterestRecyclingAmount] = useState("");
  const [interestRecyclingDate, setInterestRecyclingDate] = useState(
    new Date().toLocaleDateString("en-CA")
  );
  const [interestRecyclingNotes, setInterestRecyclingNotes] = useState("");
  const [interestRecyclingLoading, setInterestRecyclingLoading] = useState(false);
  const [showInterestRecycleHistory, setShowInterestRecycleHistory] = useState(false);

  const sortRowsByCustomer = (rows) => {
    if (!Array.isArray(rows)) return [];
    return [...rows].sort((a, b) => {
      const nameA = String(a?.customer_name || a?.name || "").trim();
      const nameB = String(b?.customer_name || b?.name || "").trim();
      return sortOrder === "az"
        ? nameA.localeCompare(nameB, undefined, { sensitivity: "base" })
        : nameB.localeCompare(nameA, undefined, { sensitivity: "base" });
    });
  };
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));

  api.get("/dashboard/today-collection", {
    params: {
      start_date: new Date().toLocaleDateString("en-CA"),
      end_date: new Date().toLocaleDateString("en-CA"),
    },
  }).then((r) => setTodayCollectionCardTotal(Number(r.data?.total) || 0));

    // Closed Loans count comes from the same authoritative
    // closed-loans endpoint used by the Closed Loans popup.
    api.get("/dashboard/closed-loans")
      .then((r) => setClosedLoansCount(Number(r.data?.count) || 0))
      .catch((e) => {
        console.error("Failed to load Closed Loans count", e);
        setClosedLoansCount(0);
      });

    api.get("/dashboard/renewed-loans").then((r) => setRenewedLoans(r.data));
    api.get("/dashboard/todays-emi", {
      params: { date: new Date().toLocaleDateString("en-CA") },
    }).then((r) => setTodaysEmi(r.data));



    // Separate Principal Recycling read.
    api.get("/principal-recycling")
      .then((r) => setPrincipalRecycling(r.data))
      .catch((e) => console.error("Failed to load principal recycling", e));

    // Separate Interest Recycling read.
    api.get("/interest-recycling")
      .then((r) => setInterestRecycling(r.data))
      .catch((e) => console.error("Failed to load interest recycling", e));
  }, []);

  useEffect(() => {
    let current = true;
    setProcessingFeeTotal(0);
    if (showProcessingFee) {
      api.get("/dashboard/processing-fee")
        .then((r) => { if (current) setProcessingFeeTotal(Number(r.data?.total) || 0); })
        .catch((err) => console.error("Processing Fee load failed:", err));
    }
    return () => { current = false; };
  }, [showProcessingFee]);

  const searchCustomers = async (query) => {
    setCustomerSearch(query);

    if (!query.trim()) {
      setCustomerSearchResults([]);
      return;
    }

    try {
      const r = await api.get("/customers", {
        params: { search: query.trim() },
      });
      setCustomerSearchResults(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      console.error("Failed to search customers", e);
      setCustomerSearchResults([]);
    }
  };

  const visible = settings?.visible_cards
    ? [...new Set([...settings.visible_cards, "principal_outstanding", "interest_outstanding", "active_loans_count", "closed_loans_count", "todays_emi", "customer_list"])]
    : Object.keys(CARD_DEFS);


  const loadEmiForDates = async (startDate, endDate) => {

    setEmiStartDate(startDate);
    setEmiEndDate(endDate);

    if (
      !startDate ||
      !endDate ||
      startDate > endDate
    ) {
      return;
    }

    try {

      const r = await api.get(
        "/dashboard/todays-emi",
        {
          params: {
            start_date: startDate,
            end_date: endDate,
          },
        }
      );

      setTodaysEmi(r.data);

      setDetails((current) =>
        current?.type === "todays_emi"
          ? {
              ...current,
              rows: r.data.rows || [],
            }
          : current
      );

    } catch (e) {

      console.error(
        "Failed to load EMI date range",
        e
      );

    }
  };

  const loadWeeklyCollectionForDates = async (startDate, endDate) => {
    setWeeklyCollectionStartDate(startDate);
    setWeeklyCollectionEndDate(endDate);

    if (!startDate || !endDate || startDate > endDate) {
      return;
    }

    try {
      const r = await api.get("/dashboard/weekly-collection", {
        params: {
          start_date: startDate,
          end_date: endDate,
        },
      });

      setDetails((current) =>
        current?.type === "weekly_collection"
          ? { ...current, rows: r.data.rows || [] }
          : current
      );
    } catch (e) {
      console.error("Failed to load weekly collection details", e);
    }
  };

  const loadTodayCollectionForDates = async (startDate, endDate) => {
    setTodayCollectionStartDate(startDate);
    setTodayCollectionEndDate(endDate);

    if (!startDate || !endDate || startDate > endDate) {
      return;
    }

    try {
      const r = await api.get("/dashboard/today-collection", {
        params: {
          start_date: startDate,
          end_date: endDate,
        },
      });

      setDetails((current) =>
        current?.type === "today_collection"
          ? { ...current, rows: r.data.rows || [] }
          : current
      );
    } catch (e) {
      console.error("Failed to load today's collection details", e);
    }
  };

  const handleCardClick = (key) => {    const k = String(key).toLowerCase();

    if (k === "customer_list") {
      api.get("/dashboard/customer-list")
        .then((r) => {
          setDetails({
            title: "Customers List",
            rows: r.data?.rows || [],
            count: Number(r.data?.count) || 0,
            total_loan_amount:
              Number(r.data?.total_loan_amount) || 0,
            type: "customer_list",
          });
        })
        .catch((e) => {
          console.error(
            "Failed to load customer list",
            e
          );

          setDetails({
            title: "Customers List",
            rows: [],
            count: 0,
            total_loan_amount: 0,
            type: "customer_list",
          });
        });

      return;
    }


    if (k === "total_lent") {
      api.get("/dashboard/total-lent-details").then((r) => {
        setDetails({
          title: "Total Money Lent",
          rows: r.data?.rows || [],
          total_disbursed: Number(r.data?.total_disbursed) || 0,
          total_renewal_cash: Number(r.data?.total_renewal_cash) || 0,
          total_money_lent: Number(r.data?.total_money_lent) || 0,
          type: "total_lent",
        });
      }).catch((e) => {
        console.error("Failed to load Total Money Lent details", e);
        setDetails({
          title: "Total Money Lent",
          rows: [],
          total_disbursed: 0,
          total_renewal_cash: 0,
          total_money_lent: 0,
          type: "total_lent",
        });
      });
      return;
    }

    
      if (k === "closed_loans_count") {
        api.get("/dashboard/closed-loans").then((r) => {
          setDetails({
            title: "Closed Loans",
            rows: r.data?.rows || [],
            total_amount: Number(r.data?.total_amount) || 0,
            type: "closed_loans",
          });
        }).catch((e) => {
          console.error("Failed to load Closed Loans details", e);
          setDetails({
            title: "Closed Loans",
            rows: [],
            total_amount: 0,
            type: "closed_loans",
          });
        });
        return;
      }

if (k === "outstanding") {
      const totalPrincipal =
        Number(stats?.principal_outstanding) || 0;

      const totalInterest =
        Number(stats?.interest_outstanding) || 0;

      setDetails({
        title: "Outstanding Balance",
        rows: stats?.outstanding_details || [],
        total_principal: totalPrincipal,
        total_interest: totalInterest,
        total_amount: Number(stats?.outstanding) || 0,
        type: "outstanding",
      });

      return;
    }


      if (k === "todays_emi") {

        const selectedDate =
          new Date().toLocaleDateString("en-CA");

        setEmiDate(selectedDate);
        setEmiStartDate(selectedDate);
        setEmiEndDate(selectedDate);

        api.get("/dashboard/todays-emi", {
          params: {
            date: selectedDate,
          },
        }).then((r) => {

          setTodaysEmi(r.data);

          setDetails({
            title: "Today's EMI",
            rows: r.data.rows || [],
            type: "todays_emi",
          });

        });

        return;
      }

      if (k === "processing_fee") {
        if (!showProcessingFee) return;
        api.get("/dashboard/processing-fee")
          .then((r) => setDetails({
            type: "processing_fee",
            data: r.data,
          }))
          .catch((err) => console.error(
            "Processing Fee details failed:",
            err
          ));
        return;
      }


    

    if (k === "weekly_collection") {
      const selectedDate = new Date().toLocaleDateString("en-CA");
      setWeeklyCollectionStartDate(selectedDate);
      setWeeklyCollectionEndDate(selectedDate);

      api.get("/dashboard/weekly-collection", {
        params: {
          start_date: selectedDate,
          end_date: selectedDate,
        },
      }).then((r) => {
        setDetails({
          title: "Weekly Collection",
          rows: r.data.rows || [],
          type: "weekly_collection",
        });
      });
      return;
    }

    if (k === "today_collection") {
      const selectedStartDate = new Date().toLocaleDateString("en-CA");
      const selectedEndDate = selectedStartDate;

      setTodayCollectionStartDate(selectedStartDate);
      setTodayCollectionEndDate(selectedEndDate);

      api.get("/dashboard/today-collection", {
        params: {
          start_date: selectedStartDate,
          end_date: selectedEndDate,
        },
      }).then((r) => {
        setDetails({
          title: "Today's Collection",
          rows: r.data?.rows || [],
          type: "today_collection",
        });
      }).catch((e) => {
        console.error("Failed to load today's collection details", e);
        setDetails({
          title: "Today's Collection",
          rows: [],
          type: "today_collection",
        });
      });

      return;
    }

    if (k === "principal_collected") {
      setDetails({
        title: "Principal Collected",
        rows: stats?.principal_details || [],
        type: "principal",
      });
      return;
    }

    if (k === "interest_collected") {
      setDetails({
        title: "Interest Collected",
        rows: stats?.interest_details || [],
        type: "interest",
      });
      return;
    }

    if (k === "upi_collected") {
      setDetails({
        title: "UPI Collected",
        rows: stats?.upi_collection_details || [],
        type: "upi_collected",
      });
      return;
    }

    if (k === "total_received") {
      setDetails({
        title: "Repayments Received",
        rows: stats?.repayment_details || [],
        type: "repayments_received",
      });
      return;
    }

    if (k === "profit_earned") {
      setDetails({
        title: "Interest / Profit",
        rows: stats?.interest_profit_details || [],
        type: "interest_profit",
      });
      return;
    }

    if (k === "active_loans_count") {
      setDetails({
        title: "Total Active Loans",
        rows: stats?.active_loans_details || [],
        type: "active_loans",
      });
      return;
    }

    if (k.includes("overdue")) {
      navigate("/overdue-customers");
      return;
    }

    if (k.includes("customer") || k.includes("borrower")) {
      navigate("/customers");
      return;
    }

    if (k === "principal_outstanding") {
      setDetails({
        title: "Principal Outstanding",
        rows: stats?.principal_outstanding_details || [],
        type: "principal_outstanding",
      });
      return;
    }

    if (k === "interest_outstanding") {
      setDetails({
        title: "Interest Outstanding",
        rows: stats?.interest_outstanding_details || [],
        total_interest: Number(stats?.interest_outstanding) || 0,
        type: "interest_outstanding",
      });
      return;
    }

    if (k === "cash_collected") {
      setDetails({
        title: "Cash Collected",
        rows: stats?.cash_collection_details || [],
        type: "cash_collected",
      });
      return;
    }

    if (
      k.includes("interest") ||
      k.includes("profit") ||
      k.includes("principal") ||
      k.includes("repayment") ||
      k.includes("today") ||
      k.includes("weekly") ||
      k.includes("cash") ||
      k.includes("upi")
    ) {
      navigate("/reports");
      return;
    }

    if (
      k.includes("lent") ||
      k.includes("outstanding") ||
      k.includes("loan")
    ) {
      navigate("/customers");
      return;
    }

    navigate("/reports");
  };

  const addPrincipalRecycling = async () => {
    const amount = Number(recyclingAmount);

    if (!Number.isFinite(amount) || amount <= 0) {
      alert("Enter a valid recycling amount.");
      return;
    }

    const available = Number(principalRecycling?.recyclable_capital) || 0;

    if (amount > available) {
      alert(
        `Only ${fmtMoney(available, currency)} principal is currently available for recycling.`
      );
      return;
    }

    try {
      setRecyclingLoading(true);

      const r = await api.post("/principal-recycling", {
        amount,
        date: recyclingDate,
        notes: recyclingNotes,
      });

      setPrincipalRecycling((current) => ({
        ...(current || {}),
        principal_collected: r.data.principal_collected,
        principal_recycled: r.data.principal_recycled,
        recyclable_capital: r.data.recyclable_capital,
        records: [
          r.data.record,
          ...((current && Array.isArray(current.records)) ? current.records : []),
        ],
      }));

      setRecyclingAmount("");
      setRecyclingNotes("");
    } catch (e) {
      console.error("Failed to recycle principal", e);
      alert(
        e?.response?.data?.detail ||
        "Failed to record principal recycling."
      );
    } finally {
      setRecyclingLoading(false);
    }
  };

  const deletePrincipalRecycling = async (id) => {
    if (!window.confirm("Delete this principal recycling record?")) {
      return;
    }

    try {
      await api.delete(`/principal-recycling/${id}`);

      setPrincipalRecycling((current) => {
        if (!current) return current;

        const records = Array.isArray(current.records)
          ? current.records.filter((r) => r.id !== id)
          : [];

        const recycled = records.reduce(
          (sum, r) => sum + (Number(r.amount) || 0),
          0
        );

        const collected = Number(current.principal_collected) || 0;

        return {
          ...current,
          principal_recycled: recycled,
          recyclable_capital: Math.max(collected - recycled, 0),
          records,
        };
      });
    } catch (e) {
      console.error("Failed to delete principal recycling", e);
      alert(
        e?.response?.data?.detail ||
        "Failed to delete recycling record."
      );
    }
  };

  const addInterestRecycling = async () => {
    const amount = Number(interestRecyclingAmount);

    if (!Number.isFinite(amount) || amount <= 0) {
      alert("Enter a valid interest recycling amount.");
      return;
    }

    const available =
      Number(interestRecycling?.recyclable_interest) || 0;

    if (amount > available) {
      alert(
        `Only ${fmtMoney(
          available,
          currency
        )} interest is currently available for recycling.`
      );
      return;
    }

    try {
      setInterestRecyclingLoading(true);

      const r = await api.post("/interest-recycling", {
        amount,
        date: interestRecyclingDate,
        notes: interestRecyclingNotes,
      });

      setInterestRecycling((current) => ({
        ...(current || {}),
        interest_collected: r.data.interest_collected,
        interest_recycled: r.data.interest_recycled,
        recyclable_interest: r.data.recyclable_interest,
        records: [
          r.data.record,
          ...(
            current && Array.isArray(current.records)
              ? current.records
              : []
          ),
        ],
      }));

      setInterestRecyclingAmount("");
      setInterestRecyclingNotes("");
    } catch (e) {
      console.error("Failed to recycle interest", e);

      alert(
        e?.response?.data?.detail ||
        "Failed to record interest recycling."
      );
    } finally {
      setInterestRecyclingLoading(false);
    }
  };


  const deleteInterestRecycling = async (id) => {
    if (!window.confirm("Delete this interest recycling record?")) {
      return;
    }

    try {
      await api.delete(`/interest-recycling/${id}`);

      setInterestRecycling((current) => {
        if (!current) return current;

        const records = Array.isArray(current.records)
          ? current.records.filter((r) => r.id !== id)
          : [];

        const recycled = records.reduce(
          (sum, r) => sum + (Number(r.amount) || 0),
          0
        );

        const collected =
          Number(current.interest_collected) || 0;

        return {
          ...current,
          interest_recycled: recycled,
          recyclable_interest: Math.max(
            collected - recycled,
            0
          ),
          records,
        };
      });
    } catch (e) {
      console.error("Failed to delete interest recycling", e);

      alert(
        e?.response?.data?.detail ||
        "Failed to delete interest recycling record."
      );
    }
  };


  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-navy tracking-tight">Dashboard</h1>
        <p className="text-slate-500 mt-1">Overview of your lending business</p>
      </div>

      <div className="relative max-w-xl">
        <input
          type="text"
          value={customerSearch}
          onChange={(e) => searchCustomers(e.target.value)}
          placeholder="Search customer name..."
          className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-accentblue"
        />

        {customerSearch.trim() && customerSearchResults.length > 0 && (
          <div className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg overflow-hidden">
            {customerSearchResults.map((customer) => (
              <button
                key={customer.id}
                type="button"
                onClick={() => navigate(`/customers/${customer.id}`)}
                className="w-full px-4 py-3 text-left hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
              >
                <div className="font-semibold text-navy">{customer.name}</div>
              </button>
            ))}
          </div>
        )}

        {customerSearch.trim() && customerSearchResults.length === 0 && (
          <div className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500 shadow-lg">
            No customer found.
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
        {visible.map((key) => {
          const def = CARD_DEFS[key];
          if (!def || (key === "processing_fee" && !showProcessingFee)) return null;
          const val = stats
            ? (
                key === "customer_list"
                  ? "Open List"
                  : key === "outstanding"
                  ? (Number(stats.outstanding) || 0)
                  : key === "total_received"
                    ? (
                        (Number(stats.principal_collected) || 0) +
                        (Number(stats.interest_collected) || 0)
                      )
                  : key === "principal_collected"
                    ? (Number(stats.principal_collected) || 0)
                    : key === "interest_collected"
                      ? Math.max(
                          (Number(stats.interest_collected) || 0) -
                          (Number(stats.total_expenses) || 0),
                          0
                        )
                      : key === "todays_emi"
                          ? (Number(todaysEmi?.total) || 0)
                          : key === "today_collection"
                            ? (Number(todayCollectionCardTotal) || 0)
                            : key === "closed_loans_count"
                              ? closedLoansCount
                        : key === "processing_fee"
                          ? (Number(processingFeeTotal) || 0)
                              : stats[key]
              )
            : 0;
          return (
      <div key={key} data-testid={`stat-card-${key}`}
        className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-all duration-150" onClick={() => handleCardClick(key)}>
        
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{def.label}</span>
                <span className={`h-10 w-10 rounded-lg ${def.bg} flex items-center justify-center ${def.color}`}>
                  {def.icon ? React.createElement(def.icon, { className: "h-5 w-5" }) : null}
                </span>
              </div>
              <div className={`mt-3 font-mono text-2xl font-bold ${def.color}`}>
                {def.money === false ? (val ?? 0) : fmtMoney(val, currency)}
              </div>
            </div>
          );
        })}
      </div>

      {/* =========================================================
          PRINCIPAL RECYCLING
          Completely separate from the existing dashboard cards.
          Does NOT modify Total Money Lent, Principal Collected,
          Interest, Outstanding, Profit, or Renewal calculations.
         ========================================================= */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="font-heading text-lg font-semibold text-navy">
                Principal Recycling
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                Move collected principal into your business capital separately.
              </p>
            </div>
            <div className="text-xs text-slate-500">
              Existing dashboard calculations are unchanged.
            </div>
          </div>
        </div>

        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Principal Collected
              </div>
              <div className="mt-2 font-mono text-2xl font-bold text-navy">
                {fmtMoney(
                  Number(
                    principalRecycling?.principal_collected ??
                    stats?.principal_collected ??
                    0
                  ),
                  currency
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Existing authoritative calculation
              </p>
            </div>

            <div className="rounded-xl border border-slate-200 bg-emerald-50 p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
                Principal Recycled
              </div>
              <div className="mt-2 font-mono text-2xl font-bold text-emerald-700">
                {fmtMoney(
                  Number(principalRecycling?.principal_recycled) || 0,
                  currency
                )}
              </div>
              <p className="text-xs text-emerald-700/70 mt-1">
                Amount explicitly moved to business capital
              </p>
            </div>

            <div className="rounded-xl border border-slate-200 bg-accentblue-light p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-accentblue">
                Recyclable Capital
              </div>
              <div className="mt-2 font-mono text-2xl font-bold text-accentblue">
                {fmtMoney(
                  Number(principalRecycling?.recyclable_capital) || 0,
                  currency
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Principal collected minus recycled amount
              </p>
            </div>
          </div>

          <div className="mt-5 rounded-xl border border-slate-200 p-4">
            <h3 className="font-semibold text-navy">Recycle Principal</h3>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
              <input
                type="number"
                min="0"
                step="0.01"
                value={recyclingAmount}
                onChange={(e) => setRecyclingAmount(e.target.value)}
                placeholder="Amount"
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accentblue"
              />

              <input
                type="date"
                value={recyclingDate}
                onChange={(e) => setRecyclingDate(e.target.value)}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accentblue"
              />

              <input
                type="text"
                value={recyclingNotes}
                onChange={(e) => setRecyclingNotes(e.target.value)}
                placeholder="Notes (optional)"
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accentblue"
              />

              <button
                type="button"
                onClick={addPrincipalRecycling}
                disabled={recyclingLoading}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
              >
                <Wallet className="h-4 w-4" />
                {recyclingLoading ? "Saving..." : "Recycle Principal"}
              </button>
            </div>

            <p className="text-xs text-slate-500 mt-2">
              Available now:{" "}
              <span className="font-semibold text-navy">
                {fmtMoney(
                  Number(principalRecycling?.recyclable_capital) || 0,
                  currency
                )}
              </span>
            </p>
          </div>

          {/* Compact Recycle History card - UI ONLY */}
          <button
            type="button"
            onClick={() => setShowRecycleHistory(true)}
            className="mt-5 w-full text-left rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md hover:border-accentblue transition-all duration-150"
          >
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-lg">♻️</span>
                  <h3 className="font-heading text-lg font-semibold text-navy">
                    Recycle History
                  </h3>
                </div>

                <p className="text-sm text-slate-500 mt-1">
                  View all principal recycling transactions
                </p>
              </div>

              <div className="text-right shrink-0">
                <div className="font-mono text-lg font-bold text-emerald-700">
                  {fmtMoney(
                    Number(principalRecycling?.principal_recycled) || 0,
                    currency
                  )}
                </div>

                <div className="text-xs text-slate-500">
                  {Array.isArray(principalRecycling?.records)
                    ? principalRecycling.records.length
                    : 0}{" "}
                  transaction{(
                    Array.isArray(principalRecycling?.records)
                      ? principalRecycling.records.length
                      : 0
                  ) === 1 ? "" : "s"}
                </div>
              </div>
            </div>

            <div className="mt-3 text-sm font-semibold text-accentblue">
              View History →
            </div>
          </button>

          {/* Recycling History popup - UI ONLY */}
          {showRecycleHistory && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
              onClick={() => setShowRecycleHistory(false)}
            >
              <div
                className="w-full max-w-3xl max-h-[85vh] overflow-hidden rounded-xl bg-white shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
                  <div>
                    <h2 className="font-heading text-xl font-bold text-navy">
                      Recycling History
                    </h2>
                    <p className="mt-1 text-sm text-slate-500">
                      All principal recycling transactions
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        printPrincipalRecyclingHistory(
                          principalRecycling?.records || [],
                          Number(principalRecycling?.principal_recycled) || 0,
                          business,
                          settings
                        )
                      }
                      disabled={
                        !Array.isArray(principalRecycling?.records) ||
                        principalRecycling.records.length === 0
                      }
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-navy text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                    >
                      <Printer className="h-4 w-4" />
                      Print / Save PDF
                    </button>

                    <button
                      type="button"
                      onClick={() => setShowRecycleHistory(false)}
                      className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                    >
                      Close
                    </button>
                  </div>
                </div>

                <div className="overflow-auto max-h-[calc(85vh-110px)]">
                  {Array.isArray(principalRecycling?.records) &&
                  principalRecycling.records.length > 0 ? (
                    <table className="w-full text-sm">
                            
                      <thead className="sticky top-0">
                        <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                          <th className="px-4 py-3">Sl.No</th>
                          <th className="px-4 py-3">Date</th>
                          <th className="px-4 py-3 text-right">Amount</th>
                          <th className="px-4 py-3">Notes</th>
                          <th className="px-4 py-3 text-right">Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {principalRecycling.records.map((record, index) => (
                          <tr
                            key={record.id}
                            className="border-t border-slate-100"
                          >
                            <td className="px-4 py-3 font-medium text-slate-600">
                              {index + 1}
                            </td>

                            <td className="px-4 py-3">
                              {fmtDate(record.date, dateFormat)}
                            </td>

                            <td className="px-4 py-3 text-right font-mono font-semibold break-words">
                              {fmtMoney(
                                Number(record.amount) || 0,
                                currency
                              )}
                            </td>

                            <td className="px-4 py-3 text-slate-600">
                              {record.notes || "-"}
                            </td>

                            <td className="px-4 py-3 text-right break-words">
                              <button
                                type="button"
                                onClick={() =>
                                  deletePrincipalRecycling(record.id)
                                }
                                className="text-red-600 hover:underline text-xs font-semibold"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>

                      <tfoot>
                        <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                          <td
                            className="px-4 py-4 text-navy break-words"
                            colSpan={2}
                          >
                            TOTAL
                          </td>

                          <td className="px-4 py-4 text-right font-mono text-emerald-700">
                            {fmtMoney(
                              Number(principalRecycling?.principal_recycled) || 0,
                              currency
                            )}
                          </td>

                          <td colSpan={2}></td>
                        </tr>
                      </tfoot>
                    </table>
                  ) : (
                    <div className="px-5 py-12 text-center text-slate-400">
                      No recycling history found.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

              
      {/* =========================================================
          INTEREST RECYCLING - SEPARATE MODULE
          Existing dashboard cards and popup formulas are unchanged.
         ========================================================= */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="font-heading text-lg font-semibold text-navy">
                Interest Recycling
              </h2>

              <p className="text-sm text-slate-500 mt-1">
                Reinvest collected net interest back into your lending capital.
              </p>
            </div>

            <div className="text-xs text-slate-500">
              Existing dashboard calculations are unchanged.
            </div>
          </div>
        </div>


        <div className="p-5">

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Net Interest Collected
              </div>

              <div className="mt-2 font-mono text-2xl font-bold text-navy">
                {fmtMoney(
                  Number(
                    interestRecycling?.interest_collected ??
                    stats?.net_interest_collected ??
                    stats?.interest_collected ??
                    0
                  ),
                  currency
                )}
              </div>

              <p className="text-xs text-slate-500 mt-1">
                Existing authoritative dashboard value
              </p>
            </div>


            <div className="rounded-xl border border-slate-200 bg-emerald-50 p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
                Interest Recycled
              </div>

              <div className="mt-2 font-mono text-2xl font-bold text-emerald-700">
                {fmtMoney(
                  Number(interestRecycling?.interest_recycled) || 0,
                  currency
                )}
              </div>

              <p className="text-xs text-emerald-700/70 mt-1">
                Interest already reinvested into business capital
              </p>
            </div>


            <div className="rounded-xl border border-slate-200 bg-accentblue-light p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-accentblue">
                Available to Recycle
              </div>

              <div className="mt-2 font-mono text-2xl font-bold text-accentblue">
                {fmtMoney(
                  Number(interestRecycling?.recyclable_interest) || 0,
                  currency
                )}
              </div>

              <p className="text-xs text-slate-500 mt-1">
                Net interest collected minus interest already recycled
              </p>
            </div>

          </div>


          <div className="mt-5 rounded-xl border border-slate-200 p-4">

            <h3 className="font-semibold text-navy">
              Recycle Interest
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">

              <input
                type="number"
                min="0"
                step="0.01"
                value={interestRecyclingAmount}
                onChange={(e) =>
                  setInterestRecyclingAmount(e.target.value)
                }
                placeholder="Amount"
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accentblue"
              />


              <input
                type="date"
                value={interestRecyclingDate}
                onChange={(e) =>
                  setInterestRecyclingDate(e.target.value)
                }
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accentblue"
              />


              <input
                type="text"
                value={interestRecyclingNotes}
                onChange={(e) =>
                  setInterestRecyclingNotes(e.target.value)
                }
                placeholder="Notes (optional)"
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accentblue"
              />


              <button
                type="button"
                onClick={addInterestRecycling}
                disabled={interestRecyclingLoading}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
              >
                <Percent className="h-4 w-4" />

                {interestRecyclingLoading
                  ? "Saving..."
                  : "Recycle Interest"}
              </button>

            </div>


            <p className="text-xs text-slate-500 mt-2">
              Available now:{" "}
              <span className="font-semibold text-navy">
                {fmtMoney(
                  Number(
                    interestRecycling?.recyclable_interest
                  ) || 0,
                  currency
                )}
              </span>
            </p>

          </div>


          <button
            type="button"
            onClick={() => setShowInterestRecycleHistory(true)}
            className="mt-5 w-full text-left rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md hover:border-accentblue transition-all duration-150"
          >
            <div className="flex items-center justify-between gap-4">

              <div>
                <div className="flex items-center gap-2">

                  <span className="text-lg">♻️</span>

                  <h3 className="font-heading text-lg font-semibold text-navy">
                    Interest Recycling History
                  </h3>

                </div>

                <p className="text-sm text-slate-500 mt-1">
                  View all interest recycling transactions
                </p>
              </div>


              <div className="text-right shrink-0">

                <div className="font-mono text-lg font-bold text-emerald-700">
                  {fmtMoney(
                    Number(
                      interestRecycling?.interest_recycled
                    ) || 0,
                    currency
                  )}
                </div>

                <div className="text-xs text-slate-500">

                  {Array.isArray(interestRecycling?.records)
                    ? interestRecycling.records.length
                    : 0}{" "}

                  transaction{(
                    Array.isArray(interestRecycling?.records)
                      ? interestRecycling.records.length
                      : 0
                  ) === 1 ? "" : "s"}

                </div>
              </div>

            </div>

            <div className="mt-3 text-sm font-semibold text-accentblue">
              View History →
            </div>
          </button>


          {showInterestRecycleHistory && (

            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
              onClick={() =>
                setShowInterestRecycleHistory(false)
              }
            >

              <div
                className="w-full max-w-3xl max-h-[85vh] overflow-hidden rounded-xl bg-white shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >

                <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">

                  <div>

                    <h2 className="font-heading text-xl font-bold text-navy">
                      Interest Recycling History
                    </h2>

                    <p className="mt-1 text-sm text-slate-500">
                      All interest reinvestment transactions
                    </p>

                  </div>


                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        printInterestRecyclingHistory(
                          interestRecycling?.records || [],
                          Number(interestRecycling?.interest_recycled) || 0,
                          business,
                          settings
                        )
                      }
                      disabled={
                        !Array.isArray(interestRecycling?.records) ||
                        interestRecycling.records.length === 0
                      }
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-navy text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                    >
                      <Printer className="h-4 w-4" />
                      Print / Save PDF
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        setShowInterestRecycleHistory(false)
                      }
                      className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                    >
                      Close
                    </button>
                  </div>

                </div>


                <div className="overflow-auto max-h-[calc(85vh-110px)]">

                  {Array.isArray(interestRecycling?.records) &&
                  interestRecycling.records.length > 0 ? (

                    <table className="w-full text-sm">

                      <thead className="sticky top-0">

                        <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">

                          <th className="px-4 py-3">
                            Sl.No
                          </th>

                          <th className="px-4 py-3">
                            Date
                          </th>

                          <th className="px-4 py-3 text-right">
                            Amount
                          </th>

                          <th className="px-4 py-3">
                            Notes
                          </th>

                          <th className="px-4 py-3 text-right">
                            Action
                          </th>

                        </tr>

                      </thead>


                      <tbody>

                        {interestRecycling.records.map(
                          (record, index) => (

                            <tr
                              key={record.id}
                              className="border-t border-slate-100"
                            >

                              <td className="px-4 py-3 font-medium text-slate-600">
                                {index + 1}
                              </td>

                              <td className="px-4 py-3">
                                {fmtDate(
                                  record.date,
                                  dateFormat
                                )}
                              </td>


                              <td className="px-4 py-3 text-right font-mono font-semibold">
                                {fmtMoney(
                                  Number(record.amount) || 0,
                                  currency
                                )}
                              </td>


                              <td className="px-4 py-3 text-slate-600">
                                {record.notes || "-"}
                              </td>


                              <td className="px-4 py-3 text-right">

                                <button
                                  type="button"
                                  onClick={() =>
                                    deleteInterestRecycling(
                                      record.id
                                    )
                                  }
                                  className="text-red-600 hover:underline text-xs font-semibold"
                                >
                                  Delete
                                </button>

                              </td>

                            </tr>

                          )
                        )}

                      </tbody>


                      <tfoot>

                        <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">

                          <td
                            className="px-4 py-4 text-navy"
                            colSpan={2}
                          >
                            TOTAL
                          </td>

                          <td className="px-4 py-4 text-right font-mono text-emerald-700">
                            {fmtMoney(
                              Number(
                                interestRecycling?.interest_recycled
                              ) || 0,
                              currency
                            )}
                          </td>

                          <td colSpan={2}></td>

                        </tr>

                      </tfoot>

                    </table>

                  ) : (

                    <div className="px-5 py-12 text-center text-slate-400">
                      No interest recycling history found.
                    </div>

                  )}

                </div>

              </div>

            </div>

          )}

        </div>
      </div>


<div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between px-5 py-4">
            <div>
              <h2 className="font-heading text-lg font-semibold text-navy">Renewed Loans</h2>
              <p className="text-sm text-slate-500 mt-1">
                View all loan renewals and the amount actually given to customers.
              </p>
            </div>
            <button
              onClick={() => setShowRenewedLoans(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-navy text-white text-sm font-semibold hover:opacity-90"
              data-testid="renewed-loans-button"
            >
              <RefreshCw className="h-4 w-4" />
              Renewed Loans
            </button>
          </div>
        </div>

        {showRenewedLoans && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-7xl max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
                <div>
                  <h2 className="font-heading text-xl font-semibold text-navy">Renewed Loans</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    New loan amount minus old outstanding = amount given
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      printRenewedLoans(
                        renewedLoans?.rows || [],
                        Number(renewedLoans?.total_amount_given) ||
                          (renewedLoans?.rows || []).reduce(
                            (sum, row) => sum + (Number(row.amount_given) || 0),
                            0
                          ),
                        business,
                        settings
                      )
                    }
                    disabled={!renewedLoans?.rows?.length}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-navy text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                  >
                    <Printer className="h-4 w-4" />
                    Print / Save PDF
                  </button>

                  <button
                    onClick={() => setShowRenewedLoans(false)}
                    className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                  >
                    Close
                  </button>
                </div>
              </div>

              <div className="overflow-auto max-h-[calc(90vh-150px)]">
                {renewedLoans?.rows?.length ? (
                  <table className="w-full text-sm">
                    <thead className="sticky top-0">
                      <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                        <th className="px-4 py-3">Sl.No</th>
                        <th className="px-4 py-3">Customer</th>
                        <th className="px-4 py-3">Renewal Date</th>
                        <th className="px-4 py-3">New Loan No</th>
                        <th className="px-4 py-3">Old Loan No</th>
                        <th className="px-4 py-3 text-right">Old Amount</th>
                        <th className="px-4 py-3 text-right">New Loan Amount</th>
                        <th className="px-4 py-3 text-right">Amount Given</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortRowsByCustomer(renewedLoans.rows).map((row, index) => (
                        <tr
                          key={`${row.loan_number}-${index}`}
                          className="border-t border-slate-100"
                        >
                          <td className="px-4 py-3 font-medium text-slate-600">
                            {index + 1}
                          </td>

                          <td className="px-4 py-3 font-medium text-navy">
                            {row.customer_name}
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {fmtDate(row.renewal_date, dateFormat)}
                          </td>
                          <td className="px-4 py-3 font-mono font-semibold text-navy">
                            {row.loan_number}
                          </td>
                          <td className="px-4 py-3 font-mono text-slate-600">
                            {row.old_loan_number || "—"}
                          </td>
                          <td className="px-4 py-3 text-right font-mono">
                            {fmtMoney(row.old_amount, currency)}
                          </td>
                          <td className="px-4 py-3 text-right font-mono">
                            {fmtMoney(row.new_amount, currency)}
                          </td>
                          <td className="px-4 py-3 text-right font-mono font-bold text-emerald-700">
                            {fmtMoney(row.amount_given, currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                        <td className="px-4 py-4 text-navy" colSpan={7}>
                          TOTAL AMOUNT GIVEN
                        </td>
                        <td className="px-4 py-4 text-right font-mono text-emerald-700">
                          {fmtMoney(renewedLoans.total_amount_given || 0, currency)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                ) : (
                  <div className="px-5 py-12 text-center text-slate-400">
                    No renewed loans found.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}




{details && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setDetails(null)}
        >
          <div
            className="w-full max-w-4xl max-h-[85vh] overflow-hidden rounded-xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b px-5 py-4">
              <div>
                <h2 className="font-heading text-xl font-bold text-navy">
                  {details.title}
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  {details.type === "customer_list"
                    ? `${details.count || 0} customers`
                    : "Customer-wise collection details"}
                </p>
              </div>
              <div className="flex items-center gap-2">

                {(
                  details.type === "total_lent" ||
                  details.type === "interest_profit" ||
                  details.type === "principal" ||
                  details.type === "interest" ||
                  details.type === "outstanding" ||
                  details.type === "principal_outstanding" ||
                  details.type === "interest_outstanding" ||
                  details.type === "active_loans"
                ) && (
                  <button
                    type="button"
                    onClick={() => {

                      if (details.type === "total_lent") {
                        printTotalMoneyLent(
                          details.rows || [],
                          Number(
                            details.total_money_lent ??
                            stats?.total_lent ??
                            0
                          ),
                          business,
                          settings
                        );
                        return;
                      }

                      if (details.type === "interest_profit") {
                        printInterestProfit(
                          details.rows || [],
                          Number(
                            stats?.profit_earned
                          ) || 0,
                          business,
                          settings
                        );
                        return;
                      }

                      if (details.type === "principal") {
                        printPrincipalCollected(
                          details.rows || [],
                          Number(
                            stats?.principal_collected
                          ) || 0,
                          business,
                          settings
                        );
                        return;
                      }

                      if (details.type === "interest") {
                        printInterestCollected(
                          details.rows || [],
                          Number(
                            stats?.interest_collected
                          ) || 0,
                          business,
                          settings
                        );
                        return;
                      }

                      if (details.type === "outstanding") {
                        printOutstandingBalance(
                          details.rows || [],
                          Number(
                            details.total_principal ??
                            stats?.principal_outstanding ??
                            0
                          ),
                          Number(
                            details.total_interest ??
                            stats?.interest_outstanding ??
                            0
                          ),
                          Number(
                            details.total_amount ??
                            stats?.outstanding ??
                            0
                          ),
                          business,
                          settings
                        );
                        return;
                      }

                      if (details.type === "active_loans") {
                        printActiveLoans(
                          details.rows || [],
                          business,
                          settings
                        );
                        return;
                      }

                      if (details.type === "principal_outstanding") {
                        printPrincipalOutstanding(
                          details.rows || [],
                          Number(
                            stats?.principal_outstanding
                          ) || 0,
                          business,
                          settings
                        );
                        return;
                      }

                      if (details.type === "interest_outstanding") {
                        printInterestOutstanding(
                          details.rows || [],
                          Number(
                            stats?.interest_outstanding
                          ) || 0,
                          business,
                          settings
                        );
                      }

                    }}
                    className="inline-flex items-center gap-2 rounded-lg bg-navy px-3 py-2 text-sm font-semibold text-white hover:opacity-90"
                  >
                    <Printer size={16} />
                    Print / Save PDF
                  </button>
                )}

                <label className="text-sm font-medium text-slate-600">Sort By</label>

                <select

                  value={sortOrder}

                  onChange={(e) => setSortOrder(e.target.value)}

                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none"

                >

                  <option value="az">A-Z</option>

                  <option value="za">Z-A</option>

                </select>

                <button

                  onClick={() => setDetails(null)}

                  className="rounded-lg px-3 py-1 text-xl text-slate-500 hover:bg-slate-100"

                >

                  ×

                </button>

              </div>
            </div>

            <div className="max-h-[65vh] overflow-auto">
                {details.type === "customer_list" ? (
                  <div className="overflow-x-auto">
                    {details.rows?.length ? (
                      <table className="w-full min-w-[680px] text-sm">
                        <thead className="sticky top-0 bg-slate-50 z-10">
                          <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                            <th className="px-4 py-3 w-16">
                              Sl.No
                            </th>

                            <th className="px-4 py-3 w-20">
                              Photo
                            </th>

                            <th className="px-4 py-3">
                              Customer Name
                            </th>

                            <th className="px-4 py-3">
                              Mobile Number
                            </th>

                            <th className="px-4 py-3 text-right">
                              Loan Amount
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {sortRowsByCustomer(
                            details.rows
                          ).map((row, index) => (
                            <tr
                              key={
                                row.customer_id ||
                                `${row.customer_name}-${index}`
                              }
                              className="border-t border-slate-100 hover:bg-slate-50"
                            >
                              <td className="px-4 py-3 font-medium text-slate-500">
                                {index + 1}
                              </td>

                              <td className="px-4 py-2">
                                {row.photo ? (
                                  <img
                                    src={row.photo}
                                    alt={row.customer_name || "Customer"}
                                    className="h-10 w-10 rounded-lg border border-slate-200 object-cover bg-slate-50"
                                  />
                                ) : (
                                  <div className="h-10 w-10 rounded-lg border border-slate-200 bg-slate-100 flex items-center justify-center text-sm font-bold text-slate-500">
                                    {(row.customer_name || "?")
                                      .charAt(0)
                                      .toUpperCase()}
                                  </div>
                                )}
                              </td>

                              <td className="px-4 py-3 font-semibold text-navy">
                                {row.customer_name || "Unknown"}
                              </td>

                              <td className="px-4 py-3 font-mono text-slate-600 whitespace-nowrap">
                                {row.phone || "—"}
                              </td>

                              <td className="px-4 py-3 text-right font-mono font-semibold text-navy whitespace-nowrap">
                                {fmtMoney(
                                  Number(row.loan_amount) || 0,
                                  currency
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>

                        <tfoot>
                          <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                            <td
                              colSpan={4}
                              className="px-4 py-4 text-navy"
                            >
                              TOTAL ACTIVE LOAN AMOUNT
                            </td>

                            <td className="px-4 py-4 text-right font-mono text-navy whitespace-nowrap">
                              {fmtMoney(
                                Number(
                                  details.total_loan_amount
                                ) || 0,
                                currency
                              )}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    ) : (
                      <div className="px-5 py-12 text-center text-slate-400">
                        No customers found.
                      </div>
                    )}
                  </div>

                ) : details.type === "processing_fee" && showProcessingFee ? (
                        <div className="px-6 py-5">
                          <div className="mb-5 flex items-center justify-between gap-4">
                            <div>
                              <h3 className="text-lg font-semibold text-navy">
                                Processing Fee Details
                              </h3>
                              <p className="mt-1 text-sm text-slate-500">
                                Processing fee collected from new loans
                              </p>
                            </div>

                            <div className="shrink-0 rounded-lg bg-slate-50 px-4 py-2 text-right">
                              <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                                Total Processing Fee
                              </div>
                              <div className="mt-1 text-lg font-bold text-navy">
                                {fmtMoney(Number(details.data?.total) || 0, currency)}
                              </div>
                            </div>
                          </div>

                          <div className="overflow-hidden rounded-lg border border-slate-200">
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b bg-slate-50">
                                    <th className="w-20 px-3 py-3 text-left font-semibold text-slate-600">
                                      Sl.No
                                    </th>
                                    <th className="w-36 px-3 py-3 text-left font-semibold text-slate-600">
                                      Date
                                    </th>
                                    <th className="px-3 py-3 text-left font-semibold text-slate-600">
                                      Customer Name
                                    </th>
                                    <th className="w-40 px-3 py-3 text-left font-semibold text-slate-600">
                                      Loan No
                                    </th>
                                    <th className="w-36 px-3 py-3 text-right font-semibold text-slate-600">
                                      Amount
                                    </th>
                                  </tr>
                                </thead>

                                <tbody>
                                  {(details.data?.rows || []).map((row, index) => (
                                    <tr
                                      key={`${row.loan_number}-${index}`}
                                      className="border-b border-slate-100 last:border-b-0"
                                    >
                                      <td className="px-3 py-3 break-words">
                                        {index + 1}
                                      </td>
                                      <td className="px-3 py-3 text-slate-600 whitespace-nowrap">
                                        {row.date
                                          ? fmtDate(row.date, dateFormat)
                                          : "—"}
                                      </td>
                                      <td className="px-3 py-3 font-medium text-slate-700 break-words">
                                        {row.customer_name || "-"}
                                      </td>
                                      <td className="px-3 py-3 text-slate-600 break-words">
                                        {row.loan_number || "-"}
                                      </td>
                                      <td className="px-3 py-3 text-right font-mono font-medium text-navy break-words">
                                        {fmtMoney(Number(row.amount) || 0, currency)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>

                                <tfoot>
                                  <tr className="bg-slate-50 font-bold">
                                    <td
                                      colSpan="4"
                                      className="px-3 py-4 text-right text-slate-700"
                                    >
                                      Total Processing Fee
                                    </td>
                                    <td className="px-3 py-4 text-right font-mono text-navy break-words">
                                      {fmtMoney(Number(details.data?.total) || 0, currency)}
                                    </td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          </div>
                        </div>
                      )

                    : details.type === "total_lent" ? (
                    <div className="overflow-x-auto">
                      {details.rows?.length ? (
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 bg-slate-50">
                            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                              <th className="px-5 py-3">SL NO</th>
                              <th className="px-5 py-3">CUSTOMER NAME</th>
                              <th className="px-5 py-3">DATE</th>
                              <th className="px-5 py-3">LOAN NO</th>
                              <th className="px-5 py-3 text-right">AMOUNT</th>
                            </tr>
                          </thead>

                          <tbody>
                            {sortRowsByCustomer(details.rows).map((row, index) => {
                              const amount =
                                (Number(row.amount_disbursed) || 0) +
                                (Number(row.amount_given_after_renewal) || 0);

                              return (
                                <tr
                                  key={`${row.loan_number || row.loan_id || index}-total-lent`}
                                  className="border-t border-slate-100"
                                >
                                  <td className="px-5 py-3 font-medium text-slate-600">
                                    {index + 1}
                                  </td>

                                  <td className="px-5 py-3 font-medium text-navy">
                                    {row.customer_name || "Unknown"}
                                  </td>

                                  <td className="px-5 py-3 text-slate-600">
                                    {row.date
                                      ? fmtDate(row.date, dateFormat)
                                      : "—"}
                                  </td>

                                  <td className="px-5 py-3 font-mono text-slate-600">
                                    {row.loan_number || "—"}
                                  </td>

                                  <td className="px-5 py-3 text-right font-mono font-semibold text-navy">
                                    {fmtMoney(amount, currency)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>

                          <tfoot>
                            <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                              <td
                                className="px-5 py-4 text-navy"
                                colSpan={4}
                              >
                                TOTAL AMOUNT
                              </td>

                              <td className="px-5 py-4 text-right font-mono text-navy">
                                {fmtMoney(
                                  details.rows.reduce(
                                    (sum, row) =>
                                      sum +
                                      (Number(row.amount_disbursed) || 0) +
                                      (Number(row.amount_given_after_renewal) || 0),
                                    0
                                  ),
                                  currency
                                )}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      ) : (
                        <div className="px-5 py-12 text-center text-slate-400">
                          No money lent records found.
                        </div>
                      )}
                    </div>

                
                  ) : details.type === "closed_loans" ? (
                    details.rows?.length ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 bg-slate-50">
                            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                              <th className="px-5 py-3">SL NO</th>
                              <th className="px-5 py-3">CUSTOMER NAME</th>
                              <th className="px-5 py-3">DATE</th>
                              <th className="px-5 py-3">LOAN NO</th>
                              <th className="px-5 py-3 text-right">AMOUNT</th>
                            </tr>
                          </thead>

                          <tbody>
                            {sortRowsByCustomer(details.rows).map((row, index) => (
                              <tr
                                key={`${row.loan_number || index}-closed-loan`}
                                className="border-t border-slate-100"
                              >
                                <td className="px-5 py-3 font-medium text-slate-600">
                                  {index + 1}
                                </td>

                                <td className="px-5 py-3 font-medium text-navy">
                                  {row.customer_name || "Unknown"}
                                </td>

                                <td className="px-5 py-3 text-slate-600">
                                  {row.date
                                    ? fmtDate(row.date, dateFormat)
                                    : "—"}
                                </td>

                                <td className="px-5 py-3 font-mono text-slate-600">
                                  {row.loan_number || "—"}
                                </td>

                                <td className="px-5 py-3 text-right font-mono font-semibold text-emerald-700">
                                  {fmtMoney(
                                    Number(row.amount) || 0,
                                    currency
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>

                          <tfoot>
                            <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                              <td
                                className="px-5 py-4 text-navy"
                                colSpan={4}
                              >
                                TOTAL AMOUNT
                              </td>

                              <td className="px-5 py-4 text-right font-mono text-emerald-700">
                                {fmtMoney(
                                  details.rows.reduce(
                                    (sum, row) =>
                                      sum + (Number(row.amount) || 0),
                                    0
                                  ),
                                  currency
                                )}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    ) : (
                      <div className="px-5 py-12 text-center text-slate-400">
                        No closed loan records found.
                      </div>
                    )

) : details.type === "outstanding" ? (
                    details.rows?.length ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 bg-slate-50">
                            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                              <th className="px-5 py-3">Sl.No</th>
                              <th className="px-5 py-3">Customer Name</th>
                              <th className="px-5 py-3">Date</th>
                              <th className="px-5 py-3">Loan No</th>
                              <th className="px-5 py-3 text-right">
                                Outstanding Balance
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {sortRowsByCustomer(details.rows).map((row, index) => (
                              <tr
                                key={`${row.loan_id || row.loan_number}-${index}`}
                                className="border-t border-slate-100"
                              >
                                <td className="px-5 py-3 font-medium text-slate-600">
                                  {index + 1}
                                </td>

                                <td className="px-5 py-3 font-medium text-navy">
                                  {row.customer_name || "Unknown"}
                                </td>

                                <td className="px-5 py-3 text-slate-600">
                                  {row.date
                                    ? fmtDate(row.date, dateFormat)
                                    : "—"}
                                </td>

                                <td className="px-5 py-3 font-mono text-slate-600">
                                  {row.loan_number || "—"}
                                </td>

                                <td className="px-5 py-3 text-right font-mono font-semibold text-navy">
                                  {fmtMoney(
                                    Number(row.amount) || 0,
                                    currency
                                  )}
                                </td>
                              </tr>
                            ))}

                            <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                              <td
                                className="px-5 py-3 text-navy"
                                colSpan={4}
                              >
                                TOTAL
                              </td>

                              <td className="px-5 py-3 text-right font-mono text-navy">
                                {fmtMoney(
                                  Number(details.total_amount) || 0,
                                  currency
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="px-5 py-12 text-center text-slate-400">
                        No outstanding balance found.
                      </div>
                    )
                  ) : details.type === "todays_emi" ? (
                  <div>
                    <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">

                      <div className="flex items-end gap-3 flex-wrap">

                        <div>
                          <label className="block mb-1 text-xs font-semibold text-slate-600">
                            Start Date
                          </label>

                          <input
                            type="date"
                            value={emiStartDate}
                            onChange={(e) => {
                              const value =
                                e.target.value;

                              setEmiStartDate(value);

                              loadEmiForDates(
                                value,
                                emiEndDate
                              );
                            }}
                            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accentblue"
                          />
                        </div>

                        <div>
                          <label className="block mb-1 text-xs font-semibold text-slate-600">
                            End Date
                          </label>

                          <input
                            type="date"
                            value={emiEndDate}
                            min={emiStartDate}
                            onChange={(e) => {
                              const value =
                                e.target.value;

                              setEmiEndDate(value);

                              loadEmiForDates(
                                emiStartDate,
                                value
                              );
                            }}
                            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accentblue"
                          />
                        </div>

                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          printTodaysEmi(
                            details.rows || [],
                            emiStartDate === emiEndDate
                              ? emiStartDate
                              : `${emiStartDate} to ${emiEndDate}`,
                            business,
                            settings
                          )
                        }
                        className="inline-flex items-center gap-2 rounded-lg bg-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
                      >
                        <Printer size={16} />
                        Print / Save PDF
                      </button>

                    </div>

                    {details.rows?.length ? (
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-slate-50">
                          <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                            <th className="px-5 py-3">Sl.No</th>
                            <th className="px-5 py-3">Customer Name</th>
                            <th className="px-5 py-3">Phone Number</th>
                            <th className="px-5 py-3">Loan No</th>
                            <th className="px-5 py-3">Date</th>
                            <th className="px-5 py-3 text-right">Principal</th>
                            <th className="px-5 py-3 text-right">Interest</th>
                            <th className="px-5 py-3 text-right">EMI Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortRowsByCustomer(details.rows).map((row, index) => (
                            <tr
                              key={`${row.loan_id || row.loan_number}-${row.week || index}`}
                              className="border-t border-slate-100"
                            >
                              <td className="px-5 py-3 font-medium text-slate-600">
                                {index + 1}
                              </td>
                              <td className="px-5 py-3 font-medium text-navy">
                                {row.customer_name}
                              </td>
                              <td className="px-5 py-3 font-mono text-slate-600">
                                {row.phone_number || "—"}
                              </td>
                              <td className="px-5 py-3 font-mono text-slate-600">
                                {row.loan_number}
                              </td>
                              <td className="px-5 py-3 text-slate-600">
                                {row.date ? fmtDate(row.date, dateFormat) : "—"}
                              </td>
                              <td className="px-5 py-3 text-right font-mono font-semibold text-navy">
                                {fmtMoney(row.principal_amount, currency)}
                              </td>
                              <td className="px-5 py-3 text-right font-mono font-semibold text-maroon">
                                {fmtMoney(row.interest_amount, currency)}
                              </td>
                              <td className="px-5 py-3 text-right font-mono font-semibold text-navy">
                                {fmtMoney(row.emi_amount, currency)}
                              </td>
                            </tr>
                          ))}
                          <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                            <td className="px-5 py-3 text-navy" colSpan={5}>
                              TOTAL
                            </td>
                            <td className="px-5 py-3 text-right font-mono text-navy">
                              {fmtMoney(
                                details.rows.reduce(
                                  (sum, row) =>
                                    sum + (Number(row.principal_amount) || 0),
                                  0
                                ),
                                currency
                              )}
                            </td>
                            <td className="px-5 py-3 text-right font-mono text-maroon">
                              {fmtMoney(
                                details.rows.reduce(
                                  (sum, row) =>
                                    sum + (Number(row.interest_amount) || 0),
                                  0
                                ),
                                currency
                              )}
                            </td>
                            <td className="px-5 py-3 text-right font-mono text-navy">
                              {fmtMoney(
                                details.rows.reduce(
                                  (sum, row) =>
                                    sum + (Number(row.emi_amount) || 0),
                                  0
                                ),
                                currency
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    ) : (
                      <div className="px-5 py-12 text-center text-slate-400">
                        No EMI due in the selected date range.
                      </div>
                    )}
                  </div>
                 ) : details.type === "interest_profit" ? (
                  details.rows?.length ? (
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-slate-50">
                        <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                          <th className="px-5 py-3">Sl.No</th>
                          <th className="px-5 py-3">Customer Name</th>
                          <th className="px-5 py-3">Date</th>
                          <th className="px-5 py-3">Loan No</th>
                          <th className="px-5 py-3 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortRowsByCustomer(details.rows).map((row, index) => (
                          <tr
                            key={`${row.loan_id || row.loan_number}-${index}`}
                            className="border-t border-slate-100"
                          >
                            <td className="px-5 py-3 font-medium text-slate-600">
                              {index + 1}
                            </td>
                            <td className="px-5 py-3 font-medium text-navy">
                              {row.customer_name}
                            </td>
                            <td className="px-5 py-3 text-slate-600">
                              {row.start_date ? fmtDate(row.start_date, dateFormat) : "—"}
                            </td>
                            <td className="px-5 py-3 font-mono text-slate-600">
                              {row.loan_number || "—"}
                            </td>
                            <td className="px-5 py-3 text-right font-mono font-semibold text-maroon">
                              {fmtMoney(row.amount, currency)}
                            </td>
                          </tr>
                        ))}
                        <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                          <td className="px-5 py-3 text-navy" colSpan={4}>
                            TOTAL
                          </td>
                          <td className="px-5 py-3 text-right font-mono text-maroon">
                            {fmtMoney(
                              details.rows.reduce(
                                (sum, row) =>
                                  sum + (Number(row.amount) || 0),
                                0
                              ),
                              currency
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  ) : (
                    <div className="px-5 py-12 text-center text-slate-400">
                      No interest profit records found.
                    </div>
                  )
                ) : details.type === "repayments_received" ? (
                  <div>
                    <div className="px-5 py-4 border-b border-slate-100 flex justify-end">
                      <button
                        type="button"
                        onClick={() =>
                          printRepaymentsReceived(
                            details.rows || [],
                            business,
                            settings
                          )
                        }
                        className="inline-flex items-center gap-2 rounded-lg bg-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
                      >
                        <Printer size={16} />
                        Print / Save PDF
                      </button>
                    </div>

                    {details.rows?.length ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 bg-slate-50">
                            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                              <th className="px-5 py-3">Sl.No</th>
                              <th className="px-5 py-3">Customer Name</th>
                              <th className="px-5 py-3">Date</th>
                              <th className="px-5 py-3">Loan No</th>
                              <th className="px-5 py-3 text-right">Principal</th>
                              <th className="px-5 py-3 text-right">Interest</th>
                              <th className="px-5 py-3 text-right">Amount Paid</th>
                            </tr>
                          </thead>

                          <tbody>
                            {sortRowsByCustomer(details.rows).map((row, index) => (
                              <tr
                                key={`${row.loan_number}-${row.date}-${index}`}
                                className="border-t border-slate-100"
                              >
                                <td className="px-5 py-3 font-medium text-slate-600">
                                  {index + 1}
                                </td>

                                <td className="px-5 py-3 font-medium text-navy">
                                  {row.customer_name}
                                </td>

                                <td className="px-5 py-3 text-slate-600">
                                  {row.date ? fmtDate(row.date, dateFormat) : "—"}
                                </td>

                                <td className="px-5 py-3 font-mono text-slate-600">
                                  {row.loan_number || "—"}
                                </td>

                                <td className="px-5 py-3 text-right font-mono font-semibold text-navy">
                                  {fmtMoney(row.principal, currency)}
                                </td>

                                <td className="px-5 py-3 text-right font-mono font-semibold text-maroon">
                                  {fmtMoney(row.interest, currency)}
                                </td>

                                <td className="px-5 py-3 text-right font-mono font-semibold text-emerald-700">
                                  {fmtMoney(row.amount_paid, currency)}
                                </td>
                              </tr>
                            ))}

                            <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                              <td className="px-5 py-3 text-navy" colSpan={4}>
                                TOTAL
                              </td>

                              <td className="px-5 py-3 text-right font-mono text-navy">{fmtMoney(
                                  Number(stats?.principal_collected) || 0,
                                  currency
                                )}</td>

                              <td className="px-5 py-3 text-right font-mono text-maroon">{fmtMoney(
                                  Number(stats?.interest_collected) || 0,
                                  currency
                                )}</td>

                              <td className="px-5 py-3 text-right font-mono text-emerald-700">{fmtMoney(
                                  (Number(stats?.principal_collected) || 0) +
                                  (Number(stats?.interest_collected) || 0),
                                  currency
                                )}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="px-5 py-12 text-center text-slate-400">
                        No repayment records found.
                      </div>
                    )}
                  </div>
                ) : details.type === "interest_outstanding" ? (
                    <div>
                      <div className="px-5 py-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                        <span className="text-sm font-semibold text-slate-600">
                          Interest Outstanding
                        </span>
                        <span className="font-mono text-lg font-bold text-maroon">
                          {fmtMoney(
                            Number(stats?.interest_outstanding) || 0,
                            currency
                          )}
                        </span>
                      </div>

                      {details.rows?.length ? (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-slate-50">
                              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                                <th className="px-5 py-3">Sl.No</th>
                                <th className="px-5 py-3">Customer Name</th>
                                <th className="px-5 py-3">Date</th>
                                <th className="px-5 py-3">Loan No</th>
                                <th className="px-5 py-3 text-right">Amount</th>
                              </tr>
                            </thead>

                            <tbody>
                              {sortRowsByCustomer(details.rows).map((row, index) => (
                                <tr
                                  key={`${row.loan_number}-${row.date}-${index}`}
                                  className="border-t border-slate-100"
                                >
                                  <td className="px-5 py-3 font-medium text-slate-600">
                                    {index + 1}
                                  </td>

                                  <td className="px-5 py-3 font-medium text-navy">
                                    {row.customer_name}
                                  </td>

                                  <td className="px-5 py-3 text-slate-600">
                                    {row.date ? fmtDate(row.date, dateFormat) : "—"}
                                  </td>

                                  <td className="px-5 py-3 font-mono text-slate-600">
                                    {row.loan_number || "—"}
                                  </td>

                                  <td className="px-5 py-3 text-right font-mono font-semibold text-maroon">
                                    {fmtMoney(row.amount, currency)}
                                  </td>
                                </tr>
                              ))}

                              <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                                <td className="px-5 py-3 text-navy" colSpan={4}>
                                  TOTAL
                                </td>

                                <td className="px-5 py-3 text-right font-mono text-navy">
                                  {fmtMoney(
                                    Number(stats?.interest_outstanding) || 0,
                                    currency
                                  )}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="px-5 py-12 text-center text-slate-400">
                          No interest outstanding records found.
                        </div>
                      )}
                    </div>
) : details.type === "cash_collected" ? (
                  details.rows?.length ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-slate-50">
                          <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                            <th className="px-5 py-3">Sl.No</th>
                            <th className="px-5 py-3">Date</th>
                            <th className="px-5 py-3">Customer Name</th>
                            <th className="px-5 py-3">Loan No</th>
                            <th className="px-5 py-3 text-right">Amount</th>
                          </tr>
                        </thead>

                        <tbody>
                          {sortRowsByCustomer(details.rows).map((row, index) => (
                            <tr
                              key={`${row.loan_number}-${row.date}-${index}`}
                              className="border-t border-slate-100"
                            >
                              <td className="px-5 py-3 font-medium text-slate-600">
                                {index + 1}
                              </td>

                              <td className="px-5 py-3 text-slate-600">
                                {row.date ? fmtDate(row.date, dateFormat) : "—"}
                              </td>

                              <td className="px-5 py-3 font-medium text-navy">
                                {row.customer_name}
                              </td>

                              <td className="px-5 py-3 font-mono text-slate-600">
                                {row.loan_number || "—"}
                              </td>

                              <td className="px-5 py-3 text-right font-mono font-semibold text-navy">
                                {fmtMoney(row.amount, currency)}
                              </td>
                            </tr>
                          ))}

                          <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                            <td className="px-5 py-3 text-navy" colSpan={4}>
                              TOTAL
                            </td>

                            <td className="px-5 py-3 text-right font-mono text-navy">
                              {fmtMoney(
                                details.rows.reduce(
                                  (sum, row) =>
                                    sum + (Number(row.amount) || 0),
                                  0
                                ),
                                currency
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="px-5 py-12 text-center text-slate-400">
                      No cash collection records found.
                    </div>
                  )
                ) : details.type === "upi_collected" ? (
                  details.rows?.length ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-slate-50">
                          <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                            <th className="px-5 py-3">Sl.No</th>
                            <th className="px-5 py-3">Date</th>
                            <th className="px-5 py-3">Customer Name</th>
                            <th className="px-5 py-3">Loan No</th>
                            <th className="px-5 py-3 text-right">Amount</th>
                          </tr>
                        </thead>

                        <tbody>
                          {sortRowsByCustomer(details.rows).map((row, index) => (
                            <tr
                              key={`${row.loan_number}-${row.date}-${index}`}
                              className="border-t border-slate-100"
                            >
                              <td className="px-5 py-3 font-medium text-slate-600">
                                {index + 1}
                              </td>

                              <td className="px-5 py-3 text-slate-600">
                                {row.date ? fmtDate(row.date, dateFormat) : "—"}
                              </td>

                              <td className="px-5 py-3 font-medium text-navy">
                                {row.customer_name}
                              </td>

                              <td className="px-5 py-3 font-mono text-slate-600">
                                {row.loan_number || "—"}
                              </td>

                              <td className="px-5 py-3 text-right font-mono font-semibold text-accentblue">
                                {fmtMoney(row.amount, currency)}
                              </td>
                            </tr>
                          ))}

                          <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                            <td className="px-5 py-3 text-navy" colSpan={4}>
                              TOTAL
                            </td>

                            <td className="px-5 py-3 text-right font-mono text-navy">
                              {fmtMoney(
                                details.rows.reduce(
                                  (sum, row) =>
                                    sum + (Number(row.amount) || 0),
                                  0
                                ),
                                currency
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="px-5 py-12 text-center text-slate-400">
                      No UPI collection records found.
                    </div>
                  )
                ) : details.type === "today_collection" ? (
                  <div>
                    <div className="px-5 py-4 border-b border-slate-100 flex flex-wrap items-end justify-between gap-3">

                      <div className="flex flex-wrap items-end gap-3">

                        <div>
                          <label className="block mb-1 text-xs font-semibold text-slate-600">
                            Start Date
                          </label>

                          <input
                            type="date"
                            value={todayCollectionStartDate}
                            onChange={(e) =>
                              loadTodayCollectionForDates(
                                e.target.value,
                                todayCollectionEndDate
                              )
                            }
                            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accentblue"
                          />
                        </div>

                        <div>
                          <label className="block mb-1 text-xs font-semibold text-slate-600">
                            End Date
                          </label>

                          <input
                            type="date"
                            value={todayCollectionEndDate}
                            min={todayCollectionStartDate}
                            onChange={(e) =>
                              loadTodayCollectionForDates(
                                todayCollectionStartDate,
                                e.target.value
                              )
                            }
                            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accentblue"
                          />
                        </div>

                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          printTodayCollection(
                            details.rows || [],
                            todayCollectionStartDate,
                            todayCollectionEndDate,
                            business,
                            settings
                          )
                        }
                        className="inline-flex items-center gap-2 rounded-lg bg-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
                      >
                        <Printer size={16} />
                        Print / Save PDF
                      </button>

                    </div>

                    {details.rows?.length ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 bg-slate-50">
                            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                              <th className="px-5 py-3">Sl.No</th>
                                <th className="px-5 py-3">Customer Name</th>
                              <th className="px-5 py-3">Date</th>
                              <th className="px-5 py-3">Loan No</th>
                              <th className="px-5 py-3 text-right">Principal</th>
                              <th className="px-5 py-3 text-right">Interest</th>
                              <th className="px-5 py-3 text-right">Total</th>
                            </tr>
                          </thead>

                          <tbody>
                            {sortRowsByCustomer(details.rows).map((row, index) => (
                              <tr
                                key={`${row.loan_number}-${row.date}-${index}`}
                                className="border-t border-slate-100"
                              >
                                <td className="px-5 py-3 font-medium text-slate-600">
                                  {index + 1}
                                </td>

                                <td className="px-5 py-3 font-medium text-navy">
                                  {row.customer_name || "Unknown"}
                                </td>

                                <td className="px-5 py-3 text-slate-600">
                                  {row.date ? fmtDate(row.date, dateFormat) : "—"}
                                </td>

                                <td className="px-5 py-3 font-mono text-slate-600">
                                  {row.loan_number || "—"}
                                </td>

                                <td className="px-5 py-3 text-right font-mono font-semibold text-navy">
                                  {fmtMoney(
                                    Number(row.principal_collected) || 0,
                                    currency
                                  )}
                                </td>

                                <td className="px-5 py-3 text-right font-mono font-semibold text-maroon">
                                  {fmtMoney(
                                    Number(row.interest_collected) || 0,
                                    currency
                                  )}
                                </td>

                                <td className="px-5 py-3 text-right font-mono font-bold text-slate-800">
                                  {fmtMoney(
                                    (Number(row.principal_collected) || 0) +
                                    (Number(row.interest_collected) || 0),
                                    currency
                                  )}
                                </td>
                              </tr>
                            ))}

                            <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                              <td className="px-5 py-3 text-navy" colSpan={4}>
                                TOTAL
                              </td>

                              <td className="px-5 py-3 text-right font-mono text-navy">
                                {fmtMoney(
                                  details.rows.reduce(
                                    (sum, row) =>
                                      sum + (Number(row.principal_collected) || 0),
                                    0
                                  ),
                                  currency
                                )}
                              </td>

                              <td className="px-5 py-3 text-right font-mono text-maroon">
                                {fmtMoney(
                                  details.rows.reduce(
                                    (sum, row) =>
                                      sum + (Number(row.interest_collected) || 0),
                                    0
                                  ),
                                  currency
                                )}
                              </td>

                              <td className="px-5 py-3 text-right font-mono text-slate-800">
                                {fmtMoney(
                                  details.rows.reduce(
                                    (sum, row) =>
                                      sum +
                                      (Number(row.principal_collected) || 0) +
                                      (Number(row.interest_collected) || 0),
                                    0
                                  ),
                                  currency
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="px-5 py-12 text-center text-slate-400">
                        No collection received for the selected date range.
                      </div>
                    )}
                  </div>
) : details.type === "weekly_collection" ? (
                  <div>
                    <div className="px-5 py-4 border-b border-slate-100 flex flex-wrap items-center gap-3">
                      <label className="text-sm font-semibold text-slate-600">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={weeklyCollectionStartDate}
                        onChange={(e) =>
                          loadWeeklyCollectionForDates(
                            e.target.value,
                            weeklyCollectionEndDate
                          )
                        }
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accentblue"
                      />

                      <label className="text-sm font-semibold text-slate-600 ml-2">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={weeklyCollectionEndDate}
                        onChange={(e) =>
                          loadWeeklyCollectionForDates(
                            weeklyCollectionStartDate,
                            e.target.value
                          )
                        }
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accentblue"
                      />

                      <span className="text-sm text-slate-500">
                        Select any start and end date
                      </span>
                    </div>

                    {details.rows?.length ? (
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-slate-50">
                          <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                            <th className="px-5 py-3">Sl.No</th>
                            <th className="px-5 py-3">Customer Name</th>
                            <th className="px-5 py-3">Date</th>
                            <th className="px-5 py-3">Loan No</th>
                            <th className="px-5 py-3 text-right">Amount Paid</th>
                          </tr>
                        </thead>

                        <tbody>
                          {sortRowsByCustomer(details.rows).map((row, index) => (
                            <tr
                              key={`${row.loan_number}-${row.date}-${index}`}
                              className="border-t border-slate-100"
                            >
                              <td className="px-5 py-3 font-medium text-slate-600">
                                {index + 1}
                              </td>
                              <td className="px-5 py-3 font-medium text-navy">
                                {row.customer_name}
                              </td>
                              <td className="px-5 py-3 text-slate-600">
                                {row.date ? fmtDate(row.date, dateFormat) : "—"}
                              </td>
                              <td className="px-5 py-3 font-mono text-slate-600">
                                {row.loan_number || "—"}
                              </td>
                              <td className="px-5 py-3 text-right font-mono font-semibold text-navy">
                                {fmtMoney(row.amount_paid, currency)}
                              </td>
                            </tr>
                          ))}

                          <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                            <td className="px-5 py-3 text-navy" colSpan={4}>
                              TOTAL
                            </td>
                            <td className="px-5 py-3 text-right font-mono text-navy">
                              {fmtMoney(
                                details.rows.reduce(
                                  (sum, row) =>
                                    sum + (Number(row.amount_paid) || 0),
                                  0
                                ),
                                currency
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    ) : (
                      <div className="px-5 py-12 text-center text-slate-400">
                        No collection received in the selected 7-day period.
                      </div>
                    )}
                  </div>
                ) : details.type === "active_loans" ? (
                details.rows?.length ? (
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-slate-50">
                      <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                        <th className="px-5 py-3">Sl.No</th>
                        <th className="px-5 py-3">Customer Name</th>
                        <th className="px-5 py-3">Date</th>
                        <th className="px-5 py-3">Loan No</th>
                        <th className="px-5 py-3 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortRowsByCustomer(details.rows).map((row, index) => (
                        <tr
                          key={`${row.loan_id || row.loan_number}-${index}`}
                          className="border-t border-slate-100"
                        >
                          <td className="px-5 py-3 font-medium text-slate-600">
                            {index + 1}
                          </td>
                          <td className="px-5 py-3 font-medium text-navy">
                            {row.customer_name}
                          </td>
                          <td className="px-5 py-3 text-slate-600">
                            {row.start_date ? fmtDate(row.start_date, dateFormat) : "—"}
                          </td>
                          <td className="px-5 py-3 font-mono text-slate-600">
                            {row.loan_number}
                          </td>
                          <td className="px-5 py-3 text-right font-mono font-semibold text-navy">
                            {fmtMoney(row.amount, currency)}
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                        <td className="px-5 py-3 text-navy" colSpan={4}>
                          TOTAL
                        </td>
                        <td className="px-5 py-3 text-right font-mono text-navy">
                          {fmtMoney(
                            details.rows.reduce(
                              (sum, row) => sum + (Number(row.amount) || 0),
                              0
                            ),
                            currency
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                ) : (
                  <div className="px-5 py-12 text-center text-slate-400">
                    No active loans found.
                  </div>
                )
               ) : details.type === "principal_outstanding" ? (
                  details.rows?.length ? (
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-slate-50">
                        <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                          <th className="px-5 py-3">Sl.No</th>
                          <th className="px-5 py-3">Customer Name</th>
                          <th className="px-5 py-3">Date</th>
                          <th className="px-5 py-3">Loan No</th>
                          <th className="px-5 py-3 text-right">
                            Principal Outstanding
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {sortRowsByCustomer(details.rows).map((row, index) => (
                          <tr
                            key={`${row.loan_id}-${details.type}`}
                            className="border-t border-slate-100"
                          >
                            <td className="px-5 py-3 font-medium text-slate-600">
                              {index + 1}
                            </td>

                            <td className="px-5 py-3 font-medium text-navy">
                              {row.customer_name}
                            </td>

                            <td className="px-5 py-3 text-slate-600">
                              {row.start_date || "-"}
                            </td>

                            <td className="px-5 py-3 font-mono text-slate-600">
                              {row.loan_number}
                            </td>

                            <td className="px-5 py-3 text-right font-mono font-semibold">
                              {fmtMoney(
                                Number(row.remaining_principal) || 0,
                                currency
                              )}
                            </td>
                          </tr>
                        ))}

                        <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                          <td
                            className="px-5 py-3 text-navy"
                            colSpan={4}
                          >
                            TOTAL
                          </td>

                          <td className="px-5 py-3 text-right font-mono text-navy">
                            {fmtMoney(
                              Number(stats?.principal_outstanding) || 0,
                              currency
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  ) : (
                    <div className="px-5 py-12 text-center text-slate-400">
                      No principal outstanding found.
                    </div>
                  )
                ) : details.rows?.length ? (
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50">
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                        {(details.type === "interest" ||
                          details.type === "principal") && (
                          <th className="px-5 py-3">Sl.No</th>
                        )}
                      <th className="px-5 py-3">Customer</th>
                      <th className="px-5 py-3">Loan No</th>

                      {details.type === "interest" ? (
                        <>
                          <th className="px-5 py-3 text-right">
                            Interest Collected
                          </th>
                          
                          
                          
                        </>
                      ) : (
                        <>
                          <th className="px-5 py-3 text-right">
                            {details.type === "principal"
                              ? "Principal Collected"
                              : "Remaining Principal"}
                          </th>

                          {details.type !== "principal" &&
                              details.type !== "principal_outstanding" && (
                            <th className="px-5 py-3 text-right">
                              Principal Paid
                            </th>
                          )}
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {sortRowsByCustomer(details.rows).map((row, index) => (
                      <tr
                        key={`${row.loan_id}-${details.type}`}
                        className="border-t border-slate-100"
                      >
                          {(details.type === "interest" ||
                            details.type === "principal") && (
                            <td className="px-5 py-3 font-medium text-slate-600">
                              {index + 1}
                            </td>
                          )}
                        <td className="px-5 py-3 font-medium text-navy">
                          {row.customer_name}
                        </td>

                        <td className="px-5 py-3 font-mono text-slate-600">
                          {row.loan_number}
                        </td>

                          {details.type === "interest" ? (
                            <>
                              <td className="px-5 py-3 text-right font-mono font-semibold">
                                {fmtMoney(row.interest_collected, currency)}
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-5 py-3 text-right font-mono font-semibold">
                                {fmtMoney(
                                  details.type === "principal"
                                    ? row.principal_collected
                                    : row.remaining_principal,
                                  currency
                                )}
                              </td>
                            </>
                          )}
                        </tr>

                                              ))}
{details.type === "principal" && (
                        <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                          <td className="px-5 py-3 text-navy">TOTAL</td>
                          <td className="px-5 py-3"></td>
                          <td className="px-5 py-3 text-right font-mono text-navy">
                            {fmtMoney(
                              Number(stats?.principal_collected) || 0,
                              currency
                            )}
                          </td>
                        </tr>
                      )}

                      {details.type === "principal_outstanding" && (
                        <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                          <td className="px-5 py-3 text-navy">TOTAL</td>
                          <td className="px-5 py-3"></td>
                          <td className="px-5 py-3 text-right font-mono text-navy">
                            {fmtMoney(
                              details.rows.reduce(
                                (sum, row) =>
                                  sum + (row.is_renewed ? 0 : (Number(row.remaining_principal) || 0)),
                                0
                              ),
                              currency
                            )}
                          </td>
                          <td className="px-5 py-3 text-right font-mono text-navy">
                            {fmtMoney(
                              details.rows.reduce(
                                (sum, row) =>
                                  sum + (Number(row.principal_collected) || 0),
                                0
                              ),
                              currency
                            )}
                          </td>
                        </tr>
                      )}
                        {details.type === "interest" && (
                          <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                            <td className="px-5 py-3 text-navy" colSpan={3}>
                              TOTAL
                            </td>
                            <td className="px-5 py-3 text-right font-mono text-navy">
                              {fmtMoney(
                                Number(stats?.interest_collected) || 0,
                                currency
                              )}
                            </td>
                          </tr>
                        )}
                  </tbody>
                </table>
              ) : (
                <div className="px-5 py-12 text-center text-slate-400">
                  No collection records found.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="flex items-center justify-between px-3 py-4 border-b border-slate-100">
          <h2 className="font-heading text-lg font-semibold text-navy">Recent Loans</h2>
          <button onClick={() => navigate("/customers")} className="text-sm text-accentblue hover:underline flex items-center gap-1" data-testid="view-all-customers-button">
            View customers <ArrowRight className="h-4 w-4" />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-3 py-3 text-center">Sl.No</th>
                <th className="px-3 py-3">Loan No</th>
                <th className="px-3 py-3">Customer</th>
                <th className="px-3 py-3">Start</th>
                <th className="px-3 py-3 text-right">Amount</th>
                <th className="px-3 py-3 text-right">Outstanding</th>
                <th className="px-3 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {stats?.recent_loans?.length ? stats.recent_loans.map((l, index) => (
                <tr key={l.id} onClick={() => navigate(`/loans/${l.id}`)} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`recent-loan-${l.id}`}>
                  <td className="px-3 py-3 text-center font-medium text-slate-600">{index + 1}</td>
                  <td className="px-3 py-3 font-mono font-semibold text-navy break-words">{l.loan_number}</td>
                  <td className="px-3 py-3 break-words">{l.customer_name}</td>
                  <td className="px-3 py-3 text-slate-500 break-words">{fmtDate(l.start_date, dateFormat)}</td>
                  <td className="px-3 py-3 text-right font-mono break-words">{fmtMoney(l.principal, currency)}</td>
                  <td className="px-3 py-3 text-right font-mono break-words">{fmtMoney(l.outstanding, currency)}</td>
                  <td className="px-3 py-3 break-words">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${l.status === "active" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {l.status}
                    </span>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-400 break-words">No loans yet. Add a customer to get started.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
